/**
 * Intelligems variant-swap selling-plan sync.
 *
 * When Intelligems' client-side script swaps a form's `<input name="id">` from a
 * control variant to a duplicate-product variant for a non-control cohort, the
 * form's `<input name="selling_plan">` is left pointing at the control variant's
 * plan. Shopify rejects that combo at /cart/add with 422
 * "Cannot apply selling plan to variant".
 *
 * Approach: Intelligems generates duplicate products with predictable handles
 * (e.g. `<control-handle>-1`, `<control-handle>-2`, ...). At page load we fetch
 * `/products/<handle>-N.js` for the control product on each form, walk the
 * returned variants' `selling_plan_allocations`, and build a map of
 * { duplicateVariantId: planId } that matches the control's default frequency.
 *
 * When Intelligems mutates the form's `<input name="id">` to a duplicate
 * variant, a MutationObserver / value-property interceptor fires, looks up the
 * new ID in the map, and writes the matching plan ID into
 * `<input name="selling_plan">`. Cart submission then carries a (variant, plan)
 * pair Shopify will accept.
 *
 * No Smart Collection, no admin publishing changes, no fetch interception —
 * Shopify's /products/<handle>.js endpoint serves these duplicates even when
 * they're unlisted from Online Store, and the form's hidden input is the only
 * thing we mutate.
 */

(function() {
  "use strict";

  const FORM_COMPONENT_TAG = "product-form-component";
  const CONFIG_SELECTOR = 'script[type="application/json"][data-ig-plan-map]';
  const VARIANT_INPUT_SELECTOR = 'input[name="id"]';
  const SELLING_PLAN_INPUT_SELECTOR = 'input[name="selling_plan"]';
  const ATTACHED_FLAG = "__igPlanSyncAttached";
  const HANDLE_SUFFIXES = ["-1", "-2", "-3", "-4", "-5"];
  const LOG_PREFIX = "[ig-plan-sync]";

  /**
   * Override map for products whose Intelligems duplicates do NOT follow the
   * standard `<control-handle>-N` convention. Standard-suffix products do
   * NOT need an entry here — they're handled automatically.
   *
   * Add an entry per non-standard control. The values list every duplicate
   * handle this control's cohorts use. Order doesn't matter; cohort matching
   * is done by tag, not array position.
   *
   * Example:
   *   "weird-control-handle": [
   *     "totally-different-handle-15-off",
   *     "another-shape-20-off",
   *   ],
   *
   * @type {Record<string, string[]>}
   */
  const HANDLE_OVERRIDES = {
    // TODO: fill in as needed
    'the-weekly-essentials-box': [
      'the-essentials-box',
      'the-essentials-box-1'
    ]
  };

  // Toggle off by setting `window.__igPlanSyncDebug = false` before load.
  const DEBUG = window.__igPlanSyncDebug !== false;

  /** @param {...any} args */
  function log(...args) {
    if (DEBUG) console.log(LOG_PREFIX, ...args);
  }
  /** @param {...any} args */
  function warn(...args) {
    if (DEBUG) console.warn(LOG_PREFIX, ...args);
  }

  /**
   * Cache product fetches by control handle so 25 cards on a collection page
   * don't trigger redundant requests for the same product set.
   * @type {Map<string, Promise<any[]>>}
   */
  const productsByHandle = new Map();

  /**
   * Reads cohort prefixes from Intelligems' public JS API
   * (`window.igData.user`). Iterates every active experiment and pulls its
   * assigned non-control test group; returns the first 8 chars of each
   * group's UUID (which is the format used in `ig_tg_<prefix>` product tags).
   *
   * Source: https://docs.intelligems.io/developer-resources/javascript-api
   *
   * @returns {Set<string>}
   */
  function readCohortPrefixesFromIgData() {
    const set = new Set();
    /** @type {any} */
    const ig = /** @type {any} */ (window).igData;
    log("[igData] window.igData =", ig);
    if (!ig) {
      log("[igData] window.igData is not present yet — returning empty");
      return set;
    }
    const user = ig.user;
    log("[igData] window.igData.user =", user, "igId =", user?.igId);
    if (!user || typeof user.getExperiments !== "function") {
      log(
        "[igData] user is missing or getExperiments is not a function — returning empty",
      );
      return set;
    }
    let experiments;
    try {
      experiments = user.getExperiments() || [];
    } catch (err) {
      warn("[igData] user.getExperiments() threw:", err);
      return set;
    }
    log(
      "[igData] getExperiments() returned",
      experiments.length,
      "experiment(s):",
      experiments,
    );
    for (const exp of experiments) {
      if (!exp || !exp.id) {
        log("[igData] skipping experiment with no id:", exp);
        continue;
      }
      let tg;
      try {
        tg = user.getTestGroup(exp.id);
      } catch (err) {
        warn("[igData] user.getTestGroup() threw for", exp.id, err);
        continue;
      }
      log(
        "[igData] experiment",
        exp.id,
        "(" + (exp.name || "?") + ") → testGroup",
        tg,
      );
      if (!tg) continue;
      if (tg.isControl) {
        log(
          "[igData] experiment",
          exp.id,
          "is control group — not adding cohort prefix",
        );
        continue;
      }
      if (typeof tg.id !== "string" || tg.id.length < 8) {
        log("[igData] testGroup.id missing/short, skipping:", tg.id);
        continue;
      }
      const prefix = tg.id.slice(0, 8).toLowerCase();
      log(
        "[igData] adding cohort prefix",
        prefix,
        "from testGroup",
        tg.id,
        "(" + (tg.name || "?") + ")",
      );
      set.add(prefix);
    }
    log("[igData] final cohort prefix set:", Array.from(set));
    return set;
  }

  /**
   * Reads the active Intelligems cohort/variation prefix(es) the user is
   * currently in. Primary source is `window.igData.user` (works in both
   * preview and production, set when the IG bundle finishes loading).
   * Falls back to `sessionStorage["ig-variation"]` for older preview sessions
   * where igData hasn't initialized yet.
   *
   * Duplicate products are tagged `ig_tg_<first-8-chars-of-cohort-uuid>`, so
   * we slice the first 8 chars of each cohort UUID to match those tags.
   *
   * @returns {Set<string>} set of cohort UUID first-8 prefixes (lower-cased)
   */
  function readCohortPrefixes() {
    return readCohortPrefixesFromIgData();
    // sessionStorage["ig-variation"] fallback intentionally disabled while
    // we verify window.igData.user works end-to-end. If reinstating, see
    // git history for the previous fallback block.
  }

  /**
   * Resolves once Intelligems' JS API is ready (`window.igData.user`
   * available) — listening for both the `ig:ready` event and the
   * `onIgReady` callback patterns IG documents. Falls through after
   * `timeoutMs` so we never hang forever if IG fails to load.
   *
   * @param {number} [timeoutMs=3000]
   * @returns {Promise<void>}
   */
  function waitForIgReady(timeoutMs = 3000) {
    return new Promise((resolve) => {
      const w = /** @type {any} */ (window);
      if (w.igData?.user) {
        log("[igData] waitForIgReady: igData.user already present, resolving immediately");
        resolve();
        return;
      }
      log("[igData] waitForIgReady: waiting for ig:ready / onIgReady (timeout " + timeoutMs + "ms)");
      const startedAt = Date.now();
      let settled = false;
      const finish = (via) => {
        if (settled) return;
        settled = true;
        log(
          "[igData] waitForIgReady: resolved via",
          via,
          "after",
          Date.now() - startedAt,
          "ms. igData.user now =",
          w.igData?.user,
        );
        resolve();
      };
      window.addEventListener("ig:ready", () => finish("ig:ready"), { once: true });
      // IG documents window.onIgReady accepting a function or an array of
      // functions. Append our callback in whichever form is already present.
      const existing = w.onIgReady;
      const cb = () => finish("onIgReady");
      if (typeof existing === "function") {
        w.onIgReady = [existing, cb];
      } else if (Array.isArray(existing)) {
        existing.push(cb);
      } else {
        w.onIgReady = cb;
      }
      setTimeout(() => finish("timeout"), timeoutMs);
    });
  }

  /**
   * Finds the duplicate product whose `ig_tg_<first-8>` tag matches any of
   * the cohort UUID prefixes the user is currently in.
   *
   * @param {any[]} products
   * @param {Set<string>} cohortPrefixes  lower-cased first-8 chars
   * @param {string} controlHandle
   * @returns {{ product: any, matchedCohortPrefix: string } | null}
   */
  function findDuplicateForCohort(products, cohortPrefixes, controlHandle) {
    if (cohortPrefixes.size === 0) return null;
    for (const product of products) {
      if (!product || product.handle === controlHandle) continue;
      const tags = product.tags || [];
      for (const tag of tags) {
        if (typeof tag !== "string") continue;
        const lower = tag.toLowerCase();
        if (!lower.startsWith("ig_tg_")) continue;
        const tagPrefix = lower.slice("ig_tg_".length);
        if (cohortPrefixes.has(tagPrefix)) {
          return { product, matchedCohortPrefix: tagPrefix };
        }
      }
    }
    return null;
  }

  /**
   * Fetches the control product plus its `-1`, `-2`, ... duplicate variants
   * so the global map has plan info for the full universe of variant IDs that
   * could plausibly land in a /cart/add request body — control or duplicate.
   *
   * @param {string} controlHandle
   * @returns {Promise<any[]>}
   */
  function getProductsForHandle(controlHandle) {
    let promise = productsByHandle.get(controlHandle);
    if (!promise) {
      promise = fetchControlAndDuplicates(controlHandle);
      productsByHandle.set(controlHandle, promise);
    }
    return promise;
  }

  /**
   * @param {string} controlHandle
   */
  async function fetchControlAndDuplicates(controlHandle) {
    const overrideHandles = HANDLE_OVERRIDES[controlHandle];
    const duplicateHandles = overrideHandles
      ? overrideHandles.slice()
      : HANDLE_SUFFIXES.map((suffix) => controlHandle + suffix);
    const handles = [controlHandle].concat(duplicateHandles);
    const fetches = handles.map(async (handle) => {
      const url = `/products/${handle}.js`;
      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    });
    const products = (await Promise.all(fetches)).filter((p) => p);
    log(
      `fetched ${products.length} product(s) for control "${controlHandle}"`,
      overrideHandles ? "(using HANDLE_OVERRIDES):" : "(using suffix pattern):",
      products.map((/** @type {any} */ p) => p.handle),
    );
    return products;
  }

  /**
   * Pulls the week-number out of a frequency-ish string. Handles:
   *   "4 weeks", "4 week", "4-week", "every 4 weeks",
   *   "4 week subscription with 15% discount"
   *
   * @param {string} value
   * @returns {number | null}
   */
  function extractWeekNumber(value) {
    const match = String(value || "")
      .toLowerCase()
      .match(/(\d+)\s*-?\s*week/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Builds a lookup of selling_plan_id → week-number from the product's
   * selling_plan_groups. The plan's "Order Frequency and Unit" option
   * carries values like "4-week"; we extract the integer.
   *
   * @param {any} product - Product object from /products/<handle>.js
   * @returns {{ planWeeks: Map<string, number>, observedOptions: string[] }}
   */
  function buildPlanWeeksIndex(product) {
    /** @type {Map<string, number>} */
    const planWeeks = new Map();
    /** @type {Set<string>} */
    const observedOptions = new Set();
    for (const group of product.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) {
        for (const option of plan.options || []) {
          if (option && option.value) observedOptions.add(String(option.value));
          // The "Order Frequency and Unit" option holds values like "4-week".
          // Plan name (e.g. "4 week subscription with 20% discount") also works
          // — we accept either by running both through extractWeekNumber.
        }
        const fromOptions = (plan.options || [])
          .map((/** @type {any} */ o) => extractWeekNumber(o && o.value))
          .find((n) => typeof n === "number");
        const fromName = extractWeekNumber(plan.name);
        const weeks =
          typeof fromOptions === "number" ? fromOptions : fromName;
        if (typeof weeks === "number" && plan.id !== undefined) {
          planWeeks.set(String(plan.id), weeks);
        }
      }
    }
    return { planWeeks, observedOptions: Array.from(observedOptions) };
  }

  /**
   * @param {any[]} products
   * @param {string} defaultFrequency
   * @returns {{ map: Record<string, string>, observedOptions: string[] }}
   */
  function buildPlanMapFromProducts(products, defaultFrequency) {
    const targetWeeks = extractWeekNumber(defaultFrequency);
    /** @type {Record<string, string>} */
    const map = {};
    /** @type {Set<string>} */
    const observedOptions = new Set();
    for (const product of products) {
      const { planWeeks, observedOptions: opts } = buildPlanWeeksIndex(product);
      opts.forEach((o) => observedOptions.add(o));
      for (const variant of product.variants || []) {
        for (const allocation of variant.selling_plan_allocations || []) {
          const planId = String(allocation.selling_plan_id);
          if (!planId) continue;
          if (targetWeeks === null) continue;
          if (planWeeks.get(planId) === targetWeeks) {
            map[String(variant.id)] = planId;
            break; // Only need one matching plan per variant
          }
        }
      }
    }
    return { map, observedOptions: Array.from(observedOptions) };
  }

  /**
   * @param {HTMLElement} formComponent
   */
  function readConfig(formComponent) {
    const script = formComponent.querySelector(CONFIG_SELECTOR);
    if (!(script instanceof HTMLElement)) {
      warn(
        "no config script found inside <product-form-component>. Plan-map snippet not rendered or wrong placement.",
        formComponent,
      );
      return null;
    }
    const controlHandle = script.dataset.controlHandle;
    const defaultFrequency = script.dataset.defaultFrequency;
    if (!controlHandle || !defaultFrequency) {
      warn("config script is missing data attributes", script);
      return null;
    }
    return {
      controlHandle,
      controlProductId: script.dataset.controlProductId,
      defaultFrequency,
    };
  }

  /**
   * @param {HTMLElement} formComponent
   * @param {Record<string, string>} planMap
   * @param {string} newVariantId
   */
  function syncSellingPlan(formComponent, planMap, newVariantId) {
    const form = formComponent.querySelector("form");
    if (!form) {
      warn("no <form> inside <product-form-component>", formComponent);
      return;
    }

    let sellingPlanInput =
      /** @type {HTMLInputElement | null} */ (
        form.querySelector(SELLING_PLAN_INPUT_SELECTOR)
      );
    const planId = planMap[newVariantId];
    const before = sellingPlanInput ? sellingPlanInput.value : "(no input)";

    if (planId) {
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement("input");
        sellingPlanInput.type = "hidden";
        sellingPlanInput.name = "selling_plan";
        form.appendChild(sellingPlanInput);
        log("created selling_plan input (was missing)");
      }
      sellingPlanInput.value = planId;
      log(
        "matched variant",
        newVariantId,
        "→ plan",
        planId,
        "(was:",
        before + ")",
      );
    } else if (sellingPlanInput && sellingPlanInput.value !== "") {
      sellingPlanInput.value = "";
      warn(
        "variant",
        newVariantId,
        "not in plan map; cleared selling_plan (was:",
        before +
        "). ATC will go through as one-time. If this variant should be subscribable, the duplicate fetch failed or the frequency didn't match.",
      );
    } else {
      log(
        "variant",
        newVariantId,
        "not in plan map; selling_plan already empty (no-op).",
      );
    }
  }

  /**
   * Patches `value` on a single input so we catch property assignments
   * (Intelligems may use `el.value = '...'` rather than setAttribute).
   *
   * @param {HTMLInputElement} input
   * @param {(newValue: string) => void} onChange
   */
  function interceptValueAssignment(input, onChange) {
    const proto = HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
    if (!descriptor || !descriptor.set || !descriptor.get) return;

    const origGet = descriptor.get;
    const origSet = descriptor.set;

    Object.defineProperty(input, "value", {
      configurable: true,
      get() {
        return origGet.call(this);
      },
      set(newValue) {
        origSet.call(this, newValue);
        onChange(String(newValue));
      },
    });
  }

  /**
   * @param {HTMLElement} formComponent
   */
  async function attach(formComponent) {
    if (
      /** @type {any} */ (formComponent)[ATTACHED_FLAG]
    ) {
      return;
    }
    const config = readConfig(formComponent);
    if (!config) return;

    const variantInput =
      /** @type {HTMLInputElement | null} */ (
        formComponent.querySelector(VARIANT_INPUT_SELECTOR)
      );
    if (!variantInput) {
      warn("no input[name='id'] inside <product-form-component>", formComponent);
      return;
    }

    /** @type {any} */ (formComponent)[ATTACHED_FLAG] = true;

    const initialVariantId = variantInput.value;
    let lastSeenVariantId = initialVariantId;
    /** @type {Record<string, string>} */
    let planMap = {};

    const initialSellingPlanInput =
      /** @type {HTMLInputElement | null} */ (
        formComponent.querySelector(SELLING_PLAN_INPUT_SELECTOR)
      );
    const initialSellingPlanValue = initialSellingPlanInput
      ? initialSellingPlanInput.value
      : "(no input)";

    log(
      "attached to form. context:",
      formComponent.dataset.sourceContext ||
      formComponent.dataset.sectionId ||
      "(unspecified)",
      "control:",
      config.controlHandle,
      "freq:",
      config.defaultFrequency,
      "initial variant:",
      initialVariantId,
      "initial selling_plan:",
      initialSellingPlanValue,
    );

    const handleChange = (newValue) => {
      if (newValue === lastSeenVariantId) return;
      log(
        "variant input changed:",
        lastSeenVariantId,
        "→",
        newValue,
        "(form:",
        formComponent.dataset.sourceContext || formComponent.dataset.sectionId,
        ")",
      );
      lastSeenVariantId = newValue;
      syncSellingPlan(formComponent, planMap, newValue);
    };

    // Set up observer immediately so we don't miss any swap, even if it
    // happens before the duplicate fetch resolves.
    const observer = new MutationObserver(() => {
      handleChange(variantInput.value);
    });
    observer.observe(variantInput, {
      attributes: true,
      attributeFilter: ["value"],
    });
    interceptValueAssignment(variantInput, handleChange);

    // Snapshot final form payload for diagnostics.
    const form = formComponent.querySelector("form");
    if (form) {
      form.addEventListener(
        "submit",
        () => {
          const fd = new FormData(form);
          /** @type {Record<string, string>} */
          const snapshot = {};
          fd.forEach((v, k) => (snapshot[k] = String(v)));
          const submittedVariant = String(snapshot.id || "");
          const submittedPlan = String(snapshot.selling_plan || "");
          const expectedPlan = planMap[submittedVariant];
          let verdict;
          if (!expectedPlan) {
            verdict = "variant NOT in plan map — Shopify will treat as one-time / may 422 if a stale plan is set.";
          } else if (expectedPlan === submittedPlan) {
            verdict = "OK — variant + selling_plan match the plan map.";
          } else {
            verdict = `MISMATCH — submitted plan ${submittedPlan} but plan map says variant ${submittedVariant} should use plan ${expectedPlan}. Shopify will 422.`;
          }
          log("form submit. payload:", snapshot, "verdict:", verdict);
        },
        true,
      );
    }

    // Fetch duplicates and build the plan map. This is async; the observer
    // is already wired, so any swap that fires before this resolves is
    // captured; we then re-sync after the map is built.
    try {
      const products = await getProductsForHandle(config.controlHandle);
      const result = buildPlanMapFromProducts(products, config.defaultFrequency);
      planMap = result.map;
      const entryCount = Object.keys(planMap).length;
      if (entryCount === 0) {
        if (products.length === 0) {
          warn(
            "plan map is empty for",
            config.controlHandle,
            "— no /products/<handle>.js responses came back.",
          );
        } else {
          warn(
            "plan map is empty for",
            config.controlHandle + ":",
            `${products.length} product(s) loaded but no plan options matched default frequency '${config.defaultFrequency}'.`,
            "Observed plan option values:",
            result.observedOptions,
          );
        }
      } else {
        log(
          "plan map built for",
          config.controlHandle,
          "with",
          entryCount,
          "entries:",
          planMap,
        );
      }

      // Proactive cohort swap: ask Intelligems which cohort this session is
      // in, find the duplicate product tagged for that cohort, and overwrite
      // the form's selling_plan with that duplicate's plan id. This is the
      // "reassign default_selling_plan_id" step — Liquid rendered the
      // control's plan, but the cohort needs the duplicate's plan so the
      // (variant, plan) pair Intelligems submits to /cart/add is valid.
      // Wait for IG's JS API to be ready so reading cohort assignment isn't
      // racing the bundle's load. Falls through after a short timeout so
      // control sessions (where IG never loads/assigns) don't hang.
      await waitForIgReady();
      const cohortPrefixes = readCohortPrefixes();
      if (cohortPrefixes.size === 0) {
        log("no cohort detected (igData.user has no non-control test group, no ig-variation in sessionStorage). Leaving selling_plan as Liquid rendered it.");
      } else {
        const match = findDuplicateForCohort(
          products,
          cohortPrefixes,
          config.controlHandle,
        );
        if (!match) {
          log(
            "cohort prefixes from URL:",
            Array.from(cohortPrefixes),
            "but no duplicate product under control",
            config.controlHandle,
            "has a matching ig_tg_<prefix> tag. Leaving selling_plan as Liquid rendered it. ALL tags on each duplicate:",
            products
              .filter((p) => p && p.handle !== config.controlHandle)
              .map((p) => ({ handle: p.handle, allTags: p.tags || [] })),
          );
        } else {
          const duplicate = match.product;
          const duplicateVariant =
            (duplicate.variants && duplicate.variants[0]) || null;
          const cohortVariantId = duplicateVariant
            ? String(duplicateVariant.id)
            : null;
          const cohortPlanId = cohortVariantId
            ? planMap[cohortVariantId]
            : null;
          if (!cohortPlanId) {
            warn(
              "matched duplicate",
              duplicate.handle,
              "for cohort prefix",
              match.matchedCohortPrefix,
              "but no plan was found for variant",
              cohortVariantId,
              "in the plan map. Cannot pre-swap selling_plan.",
            );
          } else {
            const form = formComponent.querySelector("form");
            const sellingPlanInput =
              /** @type {HTMLInputElement | null} */ (
                form && form.querySelector(SELLING_PLAN_INPUT_SELECTOR)
              );
            if (!sellingPlanInput) {
              warn(
                "ready to pre-swap to plan",
                cohortPlanId,
                "for duplicate",
                duplicate.handle,
                "but the form has no selling_plan input. Skipping.",
              );
            } else {
              const before = sellingPlanInput.value;
              sellingPlanInput.value = cohortPlanId;
              log(
                "cohort prefix",
                match.matchedCohortPrefix,
                "→ duplicate",
                duplicate.handle,
                "(variant",
                cohortVariantId + ").",
                "Pre-swapped selling_plan",
                before,
                "→",
                cohortPlanId,
              );
            }
          }
        }
      }

      // If a swap happened during the fetch, sync now against the populated map.
      if (variantInput.value !== lastSeenVariantId) {
        const v = variantInput.value;
        log(
          "variant was swapped during fetch. initial:",
          initialVariantId,
          "current:",
          v,
        );
        lastSeenVariantId = v;
        syncSellingPlan(formComponent, planMap, v);
      } else if (variantInput.value !== initialVariantId) {
        // Edge case: changed from initial before observer attached.
        syncSellingPlan(formComponent, planMap, variantInput.value);
      }
    } catch (err) {
      warn("failed to fetch duplicate products:", err);
    }
  }

  function attachAll() {
    const components = document.querySelectorAll(FORM_COMPONENT_TAG);
    log(
      "initial scan: found",
      components.length,
      "<product-form-component> nodes",
    );
    components.forEach((el) => {
      if (el instanceof HTMLElement) attach(el);
    });
  }

  function watchForNewForms() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.tagName.toLowerCase() === FORM_COMPONENT_TAG) {
            attach(node);
          } else {
            const nested = node.querySelectorAll
              ? node.querySelectorAll(FORM_COMPONENT_TAG)
              : null;
            nested?.forEach((el) => {
              if (el instanceof HTMLElement) attach(el);
            });
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Builds a `weekCount → cohortPlanId` map for the cohort-matching duplicate
   * of `controlHandle`. Returns null when the user has no cohort, or when no
   * duplicate under this control matches the cohort.
   *
   * @param {string} controlHandle
   * @returns {Promise<{ weeksToPlan: Map<number, string>, duplicate: any, matchedCohortPrefix: string } | null>}
   */
  async function buildCohortPlanIdsByWeeks(controlHandle) {
    await waitForIgReady();
    const cohortPrefixes = readCohortPrefixes();
    if (cohortPrefixes.size === 0) return null;
    const products = await getProductsForHandle(controlHandle);
    const match = findDuplicateForCohort(products, cohortPrefixes, controlHandle);
    if (!match) return null;

    /** @type {Map<number, string>} */
    const weeksToPlan = new Map();
    for (const group of match.product.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) {
        const fromOptions = (plan.options || [])
          .map((/** @type {any} */ o) => extractWeekNumber(o && o.value))
          .find((n) => typeof n === "number");
        const weeks =
          typeof fromOptions === "number"
            ? fromOptions
            : extractWeekNumber(plan.name);
        if (typeof weeks === "number" && plan.id !== undefined) {
          weeksToPlan.set(weeks, String(plan.id));
        }
      }
    }
    return {
      weeksToPlan,
      duplicate: match.product,
      matchedCohortPrefix: match.matchedCohortPrefix,
    };
  }

  /**
   * Walks every `[data-selling-plan-id]` element under `rootEl` and rewrites
   * its plan id to the cohort-duplicate's plan id for the same frequency.
   * Used by the Quick View drawer (and any other place that mounts the
   * frequency UI dynamically) so user-driven frequency changes dispatch the
   * cohort plan id, not the control's.
   *
   * Frequency for each element is read from `<option>.value` or
   * `[data-frequency]`. Anything we can't classify is skipped untouched.
   *
   * @param {Element | null | undefined} rootEl
   * @param {string} controlHandle
   * @returns {Promise<number>} count of elements remapped
   */
  async function remapSellingPlanIdsInScope(rootEl, controlHandle) {
    if (!rootEl || !controlHandle) return 0;
    let result;
    try {
      result = await buildCohortPlanIdsByWeeks(controlHandle);
    } catch (err) {
      warn("remapSellingPlanIdsInScope failed building cohort plan map:", err);
      return 0;
    }
    if (!result) {
      log(
        "remapSellingPlanIdsInScope: no cohort match for control",
        controlHandle + ". Leaving plan ids as Liquid rendered.",
      );
      return 0;
    }
    const { weeksToPlan, duplicate, matchedCohortPrefix } = result;

    const els = rootEl.querySelectorAll("[data-selling-plan-id]");
    let remapped = 0;
    /** @type {string[]} */
    const skippedReasons = [];
    for (const el of els) {
      if (!(el instanceof HTMLElement)) continue;

      let frequencyStr = null;
      if (el instanceof HTMLOptionElement) {
        frequencyStr = el.value;
      } else if (el.dataset.frequency) {
        frequencyStr = el.dataset.frequency;
      }
      if (!frequencyStr) {
        skippedReasons.push(`${el.tagName} has no frequency hint`);
        continue;
      }

      const weeks = extractWeekNumber(frequencyStr);
      if (typeof weeks !== "number") {
        skippedReasons.push(`couldn't parse weeks from "${frequencyStr}"`);
        continue;
      }

      const cohortPlanId = weeksToPlan.get(weeks);
      if (!cohortPlanId) {
        skippedReasons.push(`no cohort plan for ${weeks}-week`);
        continue;
      }

      const before = el.dataset.sellingPlanId;
      if (before === cohortPlanId) continue;
      el.dataset.sellingPlanId = cohortPlanId;
      remapped += 1;
    }

    log(
      "remapSellingPlanIdsInScope:",
      controlHandle,
      "→ duplicate",
      duplicate.handle,
      "(cohort prefix",
      matchedCohortPrefix + ").",
      "Remapped",
      remapped,
      "of",
      els.length,
      "elements.",
      skippedReasons.length ? "Skipped: " + skippedReasons.join("; ") : "",
    );
    return remapped;
  }

  // Public API for callers that mount selling-plan UI dynamically (e.g. Quick
  // View drawer). The drawer fetches the control product page HTML and mounts
  // its `gr-product-options` element, which carries the control's plan ids on
  // `[data-selling-plan-id]`. Calling `remapSellingPlanIdsInScope` after mount
  // rewrites those to the cohort's plan ids so user selections dispatch the
  // right plan to the form.
  /** @type {any} */ (window).IGPlanSync = {
    remapSellingPlanIdsInScope,
  };

  // Manual savings recalculation. Intelligems rewrites `.igPrice` and
  // `.igComparePrice` text content for cohort sessions, and its own savings
  // calculation lands negative on some products after we swapped those two
  // classes on the price/compare-at elements. Rather than rely on IG to write
  // the right value into a savings element, we compute it ourselves: read the
  // current displayed prices in the subscription option, take the absolute
  // value of the difference, and write that into any
  // `[data-gr-savings-display]` element inside the same gr-product-options.
  // Re-runs on every DOM mutation so it survives IG's async rewrites.

  const SAVINGS_SELECTOR = "[data-gr-savings-display]";

  /**
   * Hardcoded SAVE-banner text per cohort. When the user is in one of these
   * cohorts we override the dollar-amount calculation with a fixed percentage
   * label, since the cohorts are flat-percent discounts and the percentage is
   * the more meaningful number to show. Keys are the first-8 chars of the
   * cohort UUID (same scheme used to match `ig_tg_<prefix>` product tags).
   *
   * @type {Record<string, string>}
   */
  const COHORT_SAVINGS_LABELS = {
    "332f9712": "SAVE 15%", // 15% off
    "47abfb74": "SAVE 20%", // 20% off
  };

  /**
   * Discount fractions per cohort, used by the yearly-savings bullet swap.
   * Cohort yearly savings = compare-at price × fraction × 13 (deliveries/year).
   * Liquid renders the control's first-selling-plan yearly amount; for users
   * in these cohorts the JS overrides with the cohort-specific number.
   *
   * @type {Record<string, number>}
   */
  const COHORT_DISCOUNT_FRACTIONS = {
    "332f9712": 0.15, // 15% off
    "47abfb74": 0.20, // 20% off
  };

  /** @returns {number | null} */
  function getCohortDiscountFraction() {
    const prefixes = readCohortPrefixes();
    for (const prefix of prefixes) {
      if (COHORT_DISCOUNT_FRACTIONS[prefix] !== undefined) {
        return COHORT_DISCOUNT_FRACTIONS[prefix];
      }
    }
    return null;
  }

  /**
   * Returns the override SAVE label for the user's current cohort, or null if
   * none of the known cohorts match (i.e. control or an untagged session).
   *
   * @returns {string | null}
   */
  function getCohortSavingsLabel() {
    const prefixes = readCohortPrefixes();
    for (const prefix of prefixes) {
      if (COHORT_SAVINGS_LABELS[prefix]) return COHORT_SAVINGS_LABELS[prefix];
    }
    return null;
  }

  /** @param {string | null} text */
  function parseMoneyText(text) {
    if (!text) return null;
    // Strip everything except digits, dot, and comma. Treat comma as
    // thousands separator (US default for this storefront).
    const cleaned = String(text).replace(/[^\d.,-]/g, "").replace(/,/g, "");
    if (!cleaned) return null;
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
  }

  /** @param {number} value */
  function formatSavingsMoney(value) {
    // Mirrors Liquid `money_without_trailing_zeros`: $5 if whole, $5.50
    // otherwise. Storefront is USD-only so $ prefix is hardcoded.
    const fixed = value.toFixed(2);
    return "$" + fixed.replace(/\.00$/, "");
  }

  /** @param {Element} grOptions */
  function recalcSavingsForOptions(grOptions) {
    if (!(grOptions instanceof HTMLElement)) return;
    const saveSpan = grOptions.querySelector(SAVINGS_SELECTOR);
    if (!(saveSpan instanceof HTMLElement)) return;

    // If the user is in a known test cohort, show the fixed percentage label
    // instead of computing dollar savings from the displayed prices. Falls
    // through to dollar calc only for control / unknown sessions.
    const cohortLabel = getCohortSavingsLabel();
    if (cohortLabel) {
      if (saveSpan.textContent !== cohortLabel) {
        saveSpan.textContent = cohortLabel;
      }
      return;
    }

    // Look for both prices inside the subscription option. Both classes are
    // present together only there (one-time option only carries one).
    const subOption = grOptions.querySelector('[ref="subscriptionOption"]');
    const scope = subOption instanceof HTMLElement ? subOption : grOptions;

    const priceEl = scope.querySelector(".igPrice");
    const compareEl = scope.querySelector(".igComparePrice");
    if (!(priceEl instanceof HTMLElement)) return;
    if (!(compareEl instanceof HTMLElement)) return;

    const priceVal = parseMoneyText(priceEl.textContent);
    const compareVal = parseMoneyText(compareEl.textContent);
    if (priceVal == null || compareVal == null) return;

    const savings = Math.abs(priceVal - compareVal);
    const newText = "SAVE " + formatSavingsMoney(savings);
    if (saveSpan.textContent !== newText) {
      saveSpan.textContent = newText;
    }
  }

  function recalcAllSavings() {
    document.querySelectorAll(SAVINGS_SELECTOR).forEach((saveSpan) => {
      const grOptions = saveSpan.closest("gr-product-options");
      if (grOptions) recalcSavingsForOptions(grOptions);
    });
  }

  function watchSavingsForRecalc() {
    recalcAllSavings();
    const observer = new MutationObserver(() => {
      recalcAllSavings();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // Subscription-bullet yearly-savings swap. Liquid renders each bullet's
  // <li> with a `data-yearly-savings="$X"` attribute (per-product computed
  // server-side) and the inner text span carrying `data-bullet-text`. Admin
  // bullet copy still has a hardcoded "$325 every year" — we regex-swap any
  // dollar amount in that text with the per-product yearly value.
  const BULLET_DOLLAR_PATTERN = /\$[\d,]+(?:\.\d+)?/g;

  /** @param {Element} li */
  function applyYearlySavingsToBullet(li) {
    if (!(li instanceof HTMLElement)) return;
    // Nugget bundle bullets use static admin copy ($30 / $25) that should not
    // be rewritten by the per-product or cohort swap. The nugget block flags
    // its <gr-product-options> with data-nugget-mode="true".
    if (li.closest('[data-nugget-mode="true"]')) return;
    const liquidYearly = li.getAttribute("data-yearly-savings");
    if (!liquidYearly) return;
    const textSpan = li.querySelector("[data-bullet-text]");
    if (!(textSpan instanceof HTMLElement)) return;
    if (!("originalBulletText" in textSpan.dataset)) {
      textSpan.dataset.originalBulletText = textSpan.textContent || "";
    }
    const original = textSpan.dataset.originalBulletText || "";
    if (!original.match(BULLET_DOLLAR_PATTERN)) return;

    // Cohort-aware override: when the session is in a known % cohort, the
    // server-rendered yearly is the control's number. Recompute as
    // compare-at × cohort fraction × 13 from the same gr-product-options.
    let yearly = liquidYearly;
    const fraction = getCohortDiscountFraction();
    if (fraction !== null) {
      const grOptions = li.closest("gr-product-options");
      const compareEl = grOptions?.querySelector(".igPrice");
      const compareVal = parseMoneyText(compareEl?.textContent || null);
      if (compareVal != null && compareVal > 0) {
        yearly = formatSavingsMoney(compareVal * fraction * 13);
      }
    }

    const swapped = original.replace(BULLET_DOLLAR_PATTERN, yearly);
    if (textSpan.textContent !== swapped) {
      textSpan.textContent = swapped;
    }
  }

  function applyAllYearlySavings() {
    document
      .querySelectorAll("[data-yearly-savings]")
      .forEach(applyYearlySavingsToBullet);
  }

  function watchYearlySavingsBullets() {
    applyAllYearlySavings();
    const observer = new MutationObserver(applyAllYearlySavings);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      log("DOMContentLoaded fired — initializing");
      attachAll();
      watchForNewForms();
      watchSavingsForRecalc();
      watchYearlySavingsBullets();
    });
  } else {
    log("script ran after DOMContentLoaded — initializing immediately");
    attachAll();
    watchForNewForms();
    watchSavingsForRecalc();
    watchYearlySavingsBullets();
  }
})();
